import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { publishAttestation } from './attestation';
import {
    formatMsisdn,
    isPulsePay,
    isWalletMethod,
    splitPremium,
    toJsDate,
    walletBrand,
    walletPayNumber
} from './wallets';

const RECEIPT_KEY = 'pulse_receipt_print';

export function openReceiptPrint(payload) {
    window.sessionStorage.setItem(RECEIPT_KEY, JSON.stringify(payload));
    window.open('/print/receipt', '_blank', 'noopener,width=720,height=900');
}

export function readReceiptPrint() {
    try {
        const raw = window.sessionStorage.getItem(RECEIPT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
}

export async function createPaymentIntent({
    tenantId,
    policy,
    method,
    amountGMD,
    wallets,
    status
}) {
    const now = new Date().toISOString();
    const payMethod = String(method || policy.paymentMethod || 'cash').toUpperCase();
    const amount = Number(amountGMD) > 0 ? Number(amountGMD) : splitPremium(policy).grossPremium;
    const ref = await addDoc(collection(db, 'tenants', tenantId, 'paymentIntents'), {
        tenantId,
        policyId: policy.id || null,
        policyNumber: policy.policyNumber || policy.id || '',
        rail: payMethod,
        paymentMethod: payMethod,
        amountGMD: amount,
        currency: 'GMD',
        status: status || 'PENDING',
        walletNumber: isWalletMethod(payMethod) ? formatMsisdn(walletPayNumber(wallets, payMethod)) : '',
        walletBrand: walletBrand(payMethod),
        createdAt: now
    });
    return { id: ref.id, amountGMD: amount, paymentMethod: payMethod, status: status || 'PENDING' };
}

export async function recordPremiumPayment({
    tenantId,
    session,
    policy,
    customer,
    wallets,
    method,
    reference,
    amountGMD,
    activate,
    extendYear,
    paymentIntentId
}) {
    const now = new Date();
    const iso = now.toISOString();
    const amount = Number(amountGMD) > 0 ? Number(amountGMD) : splitPremium(policy).grossPremium;
    const payMethod = String(method || policy.paymentMethod || 'cash').toUpperCase();
    const walletNumber = isWalletMethod(payMethod) ? formatMsisdn(walletPayNumber(wallets, payMethod)) : '';
    const rate = Number(wallets && wallets.commissionRateBroker) || 0.10;
    const brokerId = policy.brokerId || null;
    const commission = brokerId ? Math.round(amount * rate) : 0;
    const receiptNo = 'RCPT/' + String(now.getTime()).slice(-8);
    const holder = customer || policy.policyHolder || {};
    const liveRail = isPulsePay(payMethod);

    let intentId = paymentIntentId || null;
    if (!intentId) {
        const pending = await createPaymentIntent({
            tenantId,
            policy,
            method: payMethod,
            amountGMD: amount,
            wallets,
            status: 'PENDING'
        });
        intentId = pending.id;
    }

    const patch = {
        paymentStatus: 'paid',
        paymentMethod: payMethod,
        lastPaymentAt: iso,
        lastPaymentRef: String(reference || '').trim(),
        lastReceiptNo: receiptNo,
        lastWalletNumber: walletNumber,
        lastPaymentIntentId: intentId,
        paymentConfirmedVia: liveRail ? 'pulse_pay' : 'staff_record'
    };

    if (extendYear) {
        const oldExp = toJsDate(policy.expiryDate);
        const stillInForce = oldExp && oldExp.getTime() > now.getTime();
        const start = stillInForce ? oldExp : now;
        const end = new Date(start.getTime());
        end.setFullYear(end.getFullYear() + 1);
        patch.effectiveDate = (stillInForce ? (toJsDate(policy.effectiveDate) || now) : now).toISOString();
        patch.expiryDate = end.toISOString();
        patch.renewedAt = iso;
        patch.previousExpiry = policy.expiryDate || null;
        patch.status = 'ACTIVE';
    } else if (activate || liveRail) {
        const st = String(policy.status || '').toUpperCase();
        if (st !== 'PENDING' && st !== 'QUOTE') patch.status = 'ACTIVE';
        if (st === 'QUOTE' || st === 'DRAFT' || liveRail) patch.status = 'ACTIVE';
    }

    await updateDoc(doc(db, 'tenants', tenantId, 'policies', policy.id), patch);

    try {
        await updateDoc(doc(db, 'tenants', tenantId, 'paymentIntents', intentId), {
            status: 'CONFIRMED',
            confirmedAt: iso,
            gatewayReference: String(reference || '').trim(),
            receiptNo,
            recordedBy: session.uid,
            recordedByEmail: session.email
        });
    } catch (err) {
        console.error(err);
    }

    await addDoc(collection(db, 'tenants', tenantId, 'transactions'), {
        policyId: policy.id,
        policyNumber: policy.policyNumber || policy.id,
        paymentMethod: payMethod,
        walletNumber,
        walletBrand: walletBrand(payMethod),
        reference: String(reference || '').trim(),
        amountPaidGMD: amount,
        status: 'SUCCESSFUL',
        receiptNo,
        paidAt: iso,
        recordedBy: session.uid,
        recordedByEmail: session.email
    });

    const receipt = {
        receiptNo,
        tenantId,
        companyName: session.companyName,
        policyId: policy.id,
        policyNumber: policy.policyNumber || policy.id,
        holderName: holder.fullName || '',
        holderPhone: holder.phone || '',
        plate: (policy.vehicle && policy.vehicle.registrationNumber) || '',
        amountGMD: amount,
        paymentMethod: payMethod,
        walletBrand: walletBrand(payMethod),
        walletNumber,
        reference: String(reference || '').trim(),
        paidAt: iso,
        recordedByName: session.displayName || session.email,
        brokerEmail: policy.brokerEmail || '',
        commissionGMD: commission
    };
    await addDoc(collection(db, 'tenants', tenantId, 'receipts'), receipt);

    if (brokerId && commission > 0) {
        await addDoc(collection(db, 'tenants', tenantId, 'commissions'), {
            brokerId,
            brokerEmail: policy.brokerEmail || '',
            policyId: policy.id,
            policyNumber: policy.policyNumber || policy.id,
            premiumGMD: amount,
            rate,
            amountGMD: commission,
            status: 'accrued',
            receiptNo,
            createdAt: iso
        });
    }

    const nextPolicy = { ...policy, ...patch, id: policy.id };
    try {
        await publishAttestation({
            tenantId,
            companyName: session.companyName,
            customer: holder,
            policy: nextPolicy
        });
    } catch (err) {
        console.error(err);
    }

    return { receipt, policy: nextPolicy, commission };
}
